import axios from 'axios';
import CheckBox from 'components/final-form/CheckBox';
import { DropdownOption, mapToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import { selectInput } from 'components/final-form/SelectInput';
import { textInput } from 'components/final-form/TextInput';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import { IndexedZipData } from 'components/patient/upsert/UpsertPatient';
import { sortZipData } from 'components/patient/utils/patientUtils';
import { FormApi } from 'final-form';
import createDecorator from 'final-form-calculate';
import React, { useEffect, useMemo, useState } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Header } from 'semantic-ui-react';
import { addSupplier, editSupplier } from 'services/suppliersService';
import { searchZipDataForInput } from 'services/zipDataServices';
import styled from 'styled-components';
import { ZipDataOrderBy } from 'ts-types/api.enums';
import { CountryDto, UpsertSupplierDto } from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';
import { IndexSignature } from 'utils/praxTypes';
import { emailValidator, required } from 'utils/validatorUtils';

const UpsertSupplierContent = styled(FormContent)`
  max-width: 800px;
`;

interface Props extends MessageSourceProps {
  supplierId?: number,
  supplier?: UpsertSupplierDto,
  countries: CountryDto[],
  onCancel: () => void,
  onSave: () => void
}

const cancelTokenSource = axios.CancelToken.source();

const UpsertSupplierForm = (props: Props) => {
  const {supplierId, supplier, countries, onCancel, onSave, getMessage} = props;

  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const countriesDropdown: Array<DropdownOption> = mapToDropdownOptionArray(countries, "name", "shortname");
  const [cities, setCities] = useState<Array<DropdownOption>>([]);
  const [zips, setZips] = useState<Array<DropdownOption>>([]);

  useEffect(() => {

    if(supplier) {

      const initialCities = [
        {
          key: supplier.city,
          text: supplier.city,
          value: supplier.city,
          predefinedOption: true
        }
      ];

      const initialZips = [
        {
          key: supplier.zip,
          text: supplier.zip,
          value: supplier.zip,
          predefinedOption: true
        }
      ];

      setCities(initialCities);
      setZips(initialZips);
    }
  },[supplier])

  const handleSubmit = async (values: UpsertSupplierDto) => {

    let zip = "";
    let city = "";

    if (values.zip) {
      if (values.zip.includes(', ')) {
        zip = values.zip.split(", ")[0];
      } else {
        zip = values.zip;
      }
    }

    if (values.city) {
      if (values.city.includes(', ')) {
        city = values.city.split(", ")[1];
      } else {
        city = values.city;
      }
    }

    let saveValues: UpsertSupplierDto = {
      ...values,
      zip: zip,
      city: city
    }

    const handleError = (error: any) => {

      const errorCode = error.errorCode;
      const knownErrors: Array<String> = [
          errorUtils.supplierNotFound
      ];

      const violations: Array<any> = error.violations;

      if (violations && violations.length > 0) {

        violations.forEach(violation => {

          if (knownErrors.includes(violation.errorCode)) {
            setErrorMessage(getMessage(`error.${violation.errorCode}`))
          }
        });
      } else {
        if (knownErrors.includes(errorCode)) {
          setErrorMessage(getMessage(`error.${errorCode}`))
        } else {
          setErrorMessage(getMessage('error.general'));
        }
      }
    };

    if (supplierId) {
      await editSupplier(supplierId, saveValues, cancelTokenSource)
          .then(onSave)
          .catch(e => handleError(e.response.data));
    } else {
      await addSupplier(saveValues, cancelTokenSource)
          .then(onSave)
          .catch(e => handleError(e.response.data));
    }
  }

  const initialSupplierValues = (): Partial<UpsertSupplierDto> => {

    if (supplier) {
      return supplier;
    } else {
      return {
        active: true
      };
    }
  };

  const searchZipData = (countryId: number) => (query: string) => {

    searchZipDataForInput(query, countryId, cancelTokenSource, 75)
        .then(response => {

          const allInitialAndNewCityAdditions = cities.filter(city => city.value == city.key);
          const allInitialAndNewZipAdditions = zips.filter(zip => zip.value == zip.key);

          setCities([...allInitialAndNewCityAdditions, ...mapZipData("city", response, ZipDataOrderBy.CITY)]);
          setZips([...allInitialAndNewZipAdditions, ...mapZipData("zip", response, ZipDataOrderBy.ZIP)]);

        })
        .catch(() => {
          setCities([]);
          setZips([]);
        })
  };

  const mapZipData = (field: string, zipData: Array<IndexedZipData>, orderBy: ZipDataOrderBy): Array<DropdownOption> => {

    const sortedZipData = sortZipData(zipData, orderBy);

    return sortedZipData.map(data => ({
      key: data.id,
      text: data[field],
      value: `${data.zip}, ${data.city}, ${data.countryId}`,
      content: `${data.zip}, ${data.city}, ${data.canton}`,
      predefinedOption: true
    }))
  };


  const onNewAddition = (options: "zip" | "cities") => (newAddition: DropdownOption) => {

    if (options == "cities") {

      const foundCity = cities.find(city => city.key == newAddition.key);

      if (!foundCity) {
        newAddition.predefinedOption = false;
        const newCities = cities.filter(city => city.predefinedOption === true);
        setCities([newAddition, ...newCities])
      }
    } else if (options == "zip") {

      const foundZip = zips.find(zip => zip.key == newAddition.key);

      if (!foundZip) {
        newAddition.predefinedOption = false;
        const newZips = zips.filter(city => city.predefinedOption === true);
        setZips([newAddition, ...newZips]);
      }
    }
  };

  const updateZipDataFieldDecorator = (field: string) =>
      (zipData?: string, allValues?: Partial<UpsertSupplierDto & IndexSignature>) => {

        if (zipData) {
          const splitZipData = zipData.split(", ");

          if (splitZipData.length == 3 && allValues && allValues[field] != zipData) {
            return zipData;
          }
        }

        return allValues ? allValues[field] : undefined;
      };

  const zipDataDecorator = useMemo(() => createDecorator(
      {
        field: "city",
        updates: {
          zip: updateZipDataFieldDecorator("zip")
        }
      },
      {
        field: "zip",
        updates: {
          city: updateZipDataFieldDecorator("city")
        }
      }
  ),[]);

  const renderFinalForm = (): React.ReactNode => {

    const supplier = initialSupplierValues();

    return (
        <FinalForm
            onSubmit={(values: any) => handleSubmit(values)}
            initialValues={supplier}
            subscription={{values: true, pristine: true, submitting: true}}
            decorators={[zipDataDecorator]}
            render={renderForm}
        />
    );
  }

  const renderAddressSection = (form: FormApi): React.ReactNode => {

    const validator = (value: string) => required(value);

    const selectMap: IndexSignature = {
      city: cities,
      zip: zips,
      countryId: countriesDropdown,
    };


    const addressLeftInfoFields = [
      textInput({
        name: "description",
        validate: validator
      }),
      textInput({
        name: "language"
      }),
      selectInput({
            name: "countryId",
            validate: validator,
          },
          selectMap
      )
    ];

    const addressRightInfoFields = [

      selectInput({
            name: "zip",
            allowAdditions: true,
            remoteSearch: true,
            customRemoteSearchMethod: (searchZipData(form.getState().values.countryId)),
            onNewAddition: (onNewAddition("zip")),
            validate: validator
          },
          selectMap
      ),
      selectInput({
            name: "city",
            allowAdditions: true,
            remoteSearch: true,
            customRemoteSearchMethod: (() => searchZipData(form.getState().values.countryId)),
            onNewAddition: (onNewAddition("cities")),
            validate: validator
          },
          selectMap
      ),
      textInput({
            name: "street"
          }
      )
    ];

    const mappedLeftRow = addressLeftInfoFields.map((fieldProps, index) => {

      return (
          <Grid.Row key={index}>
            <Grid.Column width={5} verticalAlign="middle">
              {`${getMessage(`supplier.${fieldProps.name}`)}:`}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  {...fieldProps}
              />
            </Grid.Column>
          </Grid.Row>
      );
    });

    const mappedRightRow = addressRightInfoFields.map((fieldProps, index) => {

      return (
          <Grid.Row key={index}>
            <Grid.Column width={5} verticalAlign="middle">
              {`${getMessage(`supplier.${fieldProps.name}`)}:`}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  {...fieldProps}
              />
            </Grid.Column>
          </Grid.Row>
      );
    });

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column width={10} verticalAlign="middle" textAlign="left">
              <Header as='h5'>{getMessage("supplier.address")}</Header>
            </Grid.Column>
            <Grid.Column width={6} textAlign="right">
              <Field
                  name="active"
                  component={CheckBox}
                  label={getMessage("guarantor.active")}
                  toggle
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8}>
              <PraxFormGrid>
                {mappedLeftRow}
              </PraxFormGrid>
            </Grid.Column>
            <Grid.Column width={8}>
              <PraxFormGrid>
                {mappedRightRow}
              </PraxFormGrid>
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  };

  const renderAdditionalInformationSection = (form: FormApi): React.ReactNode => {


    const additionalInformationLeftFields = [
      textInput({
        name: "title",
      }),
      textInput({
        name: "clientNumber"
      }),
      textInput({
        name: "position"
      }),
    ];

    const additionalInformationRightFields = [
      textInput({
        name: "tel",
      }),
      textInput({
        name: "email",
        validate: emailValidator
      }),
    ];

    const mappedLeftRow = additionalInformationLeftFields.map((fieldProps, index) => {

      return (
          <Grid.Row key={index}>
            <Grid.Column width={5} verticalAlign="middle">
              {`${getMessage(`supplier.${fieldProps.name}`)}:`}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  {...fieldProps}
              />
            </Grid.Column>
          </Grid.Row>
      );
    });

    const mappedRightRow = additionalInformationRightFields.map((fieldProps, index) => {

      return (
          <Grid.Row key={index}>
            <Grid.Column width={5} verticalAlign="middle">
              {`${getMessage(`supplier.${fieldProps.name}`)}:`}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  {...fieldProps}
              />
            </Grid.Column>
          </Grid.Row>
      );
    });

    return (
        <React.Fragment>
          <Grid.Row style={{marginTop: '2rem'}}>
            <Grid.Column width={10} verticalAlign="middle" textAlign="left">
              <Header as='h5'>{getMessage("supplier.additionalInformation")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8}>
              <PraxFormGrid>
                {mappedLeftRow}
              </PraxFormGrid>
            </Grid.Column>
            <Grid.Column width={8} >
              <PraxFormGrid>
                {mappedRightRow}
              </PraxFormGrid>
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  };

  const renderForm = ({handleSubmit, submitting, form}: FormRenderProps): React.ReactNode => {
    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <UpsertSupplierContent>
              {errorMessage
                  && (
                      <StyledErrorMessage onDismiss={() => setErrorMessage(undefined)}>
                        {errorMessage}
                      </StyledErrorMessage>
                  )
              }
              <PraxFormGrid>
                {renderAddressSection(form)}
                {renderAdditionalInformationSection(form)}
              </PraxFormGrid>
            </UpsertSupplierContent>
            <FormButtonsRow>
              <Button compact type="Submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };
  return (
      <>
        {renderFinalForm()}
      </>
  )
}
export default withMessages(UpsertSupplierForm);
